import polygonClipping from 'polygon-clipping';
import type { NwiMappingCoverage, RefdataFeature, RefdataLatLng, RefdataResultMeta } from '../../types/refdata';
import { refdataBbox, refdataContains, tryRefdata } from '../refdata/refdataClient';

export function nwiMappingIsAuthoritative(props: RefdataFeature['props']): boolean {
  const text = (key: string) => String(props[key] ?? '').trim();
  return text('STATUS').toLowerCase() === 'digital'
    && !/scalable/i.test(text('SOURCE_TYPE'))
    && !/\bno[ -]?photo\b/i.test(text('COMMENTS'));
}

function planarArea(polygons: polygonClipping.MultiPolygon): number {
  return polygons.reduce((total, polygon) => total + polygon.reduce((sum, ring, index) => {
    const twice = ring.reduce((value, point, i) => {
      const next = ring[(i + 1) % ring.length]!;
      return value + point[0] * next[1] - next[0] * point[1];
    }, 0);
    return sum + (index === 0 ? 1 : -1) * Math.abs(twice) / 2;
  }, 0), 0);
}

export function assessNwiCoverageFeatures(
  features: RefdataFeature[],
  polygon?: readonly RefdataLatLng[],
): Omit<NwiMappingCoverage, 'version'> {
  const qualified = features.filter(feature => nwiMappingIsAuthoritative(feature.props));
  // Explicit invalid-source envelopes are conservative uncertainty masks.
  // Ordinary metadata (including No Data projects) is never inferred to be
  // such a mask; the compiler preserves the raw failed source separately.
  const unavailable = features.filter(feature => String(feature.props.STATUS ?? '').trim().toLowerCase() === 'source_unavailable');
  if (!polygon && unavailable.length > 0) return { status: 'unknown', mappedFraction: 0, reason: 'Point overlaps an unavailable native wetland source; absence cannot be established.' };
  if (!polygon) return qualified.length > 0
    ? { status: 'complete', mappedFraction: 1, reason: 'Point lies within published Digital mapping coverage.' }
    : { status: 'unknown', mappedFraction: 0, reason: 'No authoritative Digital mapping coverage at this point.' };
  try {
    const ring: polygonClipping.Ring = polygon.map(p => [p.lng, p.lat]);
    const target: polygonClipping.MultiPolygon = [[ring]];
    const area = planarArea(target);
    if (!(area > 0)) throw new Error('invalid target');
    let mapped: polygonClipping.MultiPolygon = [];
    let unavailableUnion: polygonClipping.MultiPolygon = [];
    for (const feature of [...qualified, ...unavailable]) {
      const geometry = feature.geom.type === 'Polygon' ? [feature.geom.coordinates]
        : feature.geom.type === 'MultiPolygon' ? feature.geom.coordinates : null;
      if (!geometry || geometry.some(poly => poly.some(r => r.length < 4 || r.some(p => !p.every(Number.isFinite))))) throw new Error('invalid coverage geometry');
      if (unavailable.includes(feature)) unavailableUnion = polygonClipping.union(unavailableUnion, geometry);
      else mapped = polygonClipping.union(mapped, geometry);
    }
    if (unavailableUnion.length) mapped = polygonClipping.difference(mapped, unavailableUnion);
    const gap = planarArea(polygonClipping.difference(target, mapped));
    const mappedFraction = Math.max(0, Math.min(1, 1 - gap / area));
    return mappedFraction >= 1 - 1e-8
      ? { status: 'complete', mappedFraction: 1, reason: 'Entire query polygon lies within published Digital mapping coverage.' }
      : { status: mappedFraction > 0 ? 'partial' : 'unknown', mappedFraction, reason: unavailable.length ? 'Query includes unavailable native wetland source geometry; those envelopes remain unresolved even within Digital mapping coverage.' : 'Query includes land without authoritative Digital mapping coverage; absence of wetlands is unresolved there.' };
  } catch {
    return { status: 'unknown', reason: 'Mapping coverage geometry could not be verified.' };
  }
}

/** The inventory and mapping companion must describe the same immutable source release. */
export async function nwiMappingCoverage(
  wetlands: RefdataResultMeta | null,
  target: { point: RefdataLatLng; polygon?: readonly RefdataLatLng[] },
): Promise<NwiMappingCoverage> {
  const expected = wetlands?.coverage;
  if (!wetlands || expected?.datasetId !== 'nwi-mapping-coverage' || expected.version !== wetlands.version) {
    return { status: 'unknown', reason: 'Wetlands source has no matching mapping-coverage receipt.' };
  }
  const result = await tryRefdata('nwi-mapping-coverage', async () => {
    if (!target.polygon) return refdataContains('nwi-mapping-coverage', target.point);
    const lat = target.polygon.map(p => p.lat), lng = target.polygon.map(p => p.lng);
    return refdataBbox('nwi-mapping-coverage', {
      minLat: Math.min(...lat), maxLat: Math.max(...lat), minLng: Math.min(...lng), maxLng: Math.max(...lng),
    }, 2000);
  });
  if (!result || result.datasetId !== expected.datasetId || result.version !== expected.version
      || ('truncated' in result && result.truncated !== false)) {
    return { status: 'unknown', reason: 'Matching complete mapping-coverage data is unavailable or truncated.' };
  }
  return { ...assessNwiCoverageFeatures(result.features, target.polygon), version: result.version };
}
